/**
 * Источники счётчиков непрочитанного. Запускаются при первом подписчике
 * (useUnread) и останавливаются, когда подписчиков не осталось.
 *
 * - уведомления: GET /api/notifications/unread-summary при старте, по событию
 *   сокета unread_count (данные приходят сразу), по fire_on/fire_off/notification,
 *   по notices:changed (прочтение в этой вкладке) и storage (в другой вкладке),
 *   при возврате на вкладку; запасной опрос раз в минуту, пока сокет не подключён;
 * - мессенджер: сообщение teamtools-messenger-unread из окна мессенджера
 *   (встроенного или вынесенного во вкладку).
 */

import {
  noticesChangedEventName,
  noticesChangedStorageKey,
} from "@/api/notices";
import { messengerOriginsMatch } from "@/utils/messengerOrigin";

import { getUnreadSummary } from "./api";
import { setMessengerCount, setNotices } from "./unreadStore";

const MESSENGER_UNREAD_TYPE = "teamtools-messenger-unread";
const FALLBACK_POLL_MS = 60000;
const REFRESH_DEBOUNCE_MS = 250;

let subscribers = 0;
let socket = null;
let messengerOrigin = "*";
let requestSeq = 0;
let refreshTimer = null;
let pollTimer = null;

const refreshNow = async () => {
  const seq = ++requestSeq;
  try {
    const summary = await getUnreadSummary();
    if (seq === requestSeq) {
      setNotices(summary);
    }
  } catch (error) {
    // Оставляем прежние значения, следующий повод обновит.
  }
};

const refresh = () => {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(refreshNow, REFRESH_DEBOUNCE_MS);
};

const onUnreadCount = (payload) => {
  if (!payload || !Number.isFinite(Number(payload.count))) {
    refresh();
    return;
  }
  // Ответ сокета новее любого запроса, который ещё в пути.
  requestSeq += 1;
  setNotices({ count: Number(payload.count), latest: payload.latest || null });
};

const onStorage = (event) => {
  if (event.key === noticesChangedStorageKey) {
    refresh();
  }
};

const onVisibility = () => {
  if (!document.hidden) {
    refresh();
  }
};

const onMessage = (event) => {
  if (event.data?.type !== MESSENGER_UNREAD_TYPE) {
    return;
  }
  if (!messengerOriginsMatch(messengerOrigin, event.origin)) {
    return;
  }
  setMessengerCount(Number(event.data?.payload?.unreadCount));
};

const SOCKET_REFRESH_EVENTS = ["fire_on", "fire_off", "notification", "connect"];

const attachSocket = (nextSocket) => {
  if (socket === nextSocket) {
    return;
  }
  detachSocket();
  socket = nextSocket;
  if (!socket) {
    return;
  }
  socket.on("unread_count", onUnreadCount);
  SOCKET_REFRESH_EVENTS.forEach((name) => socket.on(name, refresh));
};

const detachSocket = () => {
  if (!socket) {
    return;
  }
  socket.off("unread_count", onUnreadCount);
  SOCKET_REFRESH_EVENTS.forEach((name) => socket.off(name, refresh));
  socket = null;
};

const startPolling = () => {
  pollTimer = setInterval(() => {
    if (!socket?.connected && !document.hidden) {
      refreshNow();
    }
  }, FALLBACK_POLL_MS);
};

export const resolveMessengerOrigin = (config) => {
  if (config?.messenger_client_origin) {
    return config.messenger_client_origin;
  }
  try {
    return new URL(config?.messenger_iframe_url || "").origin;
  } catch (error) {
    return "*";
  }
};

/** Обновить источники, которые зависят от контекста (сокет, адрес мессенджера). */
export const updateUnreadSources = ({ socket: nextSocket, config }) => {
  if (config) {
    messengerOrigin = resolveMessengerOrigin(config);
  }
  if (subscribers > 0 && nextSocket !== undefined) {
    attachSocket(nextSocket);
  }
};

export const startUnreadSources = () => {
  subscribers += 1;
  if (subscribers > 1) {
    return;
  }
  window.addEventListener(noticesChangedEventName, refresh);
  window.addEventListener("storage", onStorage);
  window.addEventListener("message", onMessage);
  document.addEventListener("visibilitychange", onVisibility);
  startPolling();
  refreshNow();
};

export const stopUnreadSources = () => {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers > 0) {
    return;
  }
  window.removeEventListener(noticesChangedEventName, refresh);
  window.removeEventListener("storage", onStorage);
  window.removeEventListener("message", onMessage);
  document.removeEventListener("visibilitychange", onVisibility);
  clearInterval(pollTimer);
  clearTimeout(refreshTimer);
  detachSocket();
};
